import { useMutation, useQuery } from '@apollo/client';
import { Breadcrumbs } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Link } from 'gatsby';
import gql from 'graphql-tag';
import React, { useEffect, useState } from 'react';
import Linkify from 'react-linkify';
import { StringParam, useQueryParam } from 'use-query-params';
import useCurrentUser from '../hooks/useCurrentUser';
import Header from './Header';
import Footer from './Footer';
import Layout from './Layout';
import { isMobile } from './styles';
import DivisionStep from './track/DivisionStep';
import IdentityVerificationStep from './track/IdentityVerificationStep';
import PaymentStep from './track/PaymentStep';
import { TrackDescription } from './track/TrackDescription';
import { TrackHeader } from './track/TrackHeader';
import { TrackLoginStep } from './track/TrackLoginStep';
import { TrackStepper } from './track/TrackStepper';
import { useTrackPage } from './track/useTrackPage';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    pageContainer: {
      backgroundColor: '#f8f9fa',
    },
    contentContainer: {
      maxWidth: 960,
      margin: '24px auto',
      [theme.breakpoints.down('xs')]: {
        margin: '0 auto',
        marginTop: -20,
        boxShadow: 'none',
        padding: theme.spacing(2),
      },
      backgroundColor: 'white',
      padding: theme.spacing(4),
      borderRadius: theme.shape.borderRadius,
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      textAlign: 'center',
    },
    avatar: {
      marginBottom: theme.spacing(2),
      width: '5rem',
      height: '5rem',
      margin: '0 auto',
    },
    instructions: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      whiteSpace: 'pre-line',
      color: theme.palette.text.secondary,
    },
    button: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      padding: theme.spacing(1, 4),
    },
    accept: {
      padding: theme.spacing(1),
    },
    descriptionContainer: {
      textAlign: 'left',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4),
      padding: theme.spacing(3),
      backgroundColor: '#f8f9fa',
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.grey[200]}`,
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(2),
      },
    },
    badgeContainer: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center',
      height: 28,
      borderRadius: 14,
      color: 'white',
      backgroundColor: theme.palette.grey[600],
      fontSize: 13,
    },
    openApp: {
      display: 'none',
      [theme.breakpoints.down('xs')]: {
        marginTop: 20,
        display: 'block',
      },
    },
    stepper: {
      backgroundColor: 'transparent',
      marginBottom: theme.spacing(3),
    },
    featuredImage: {
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(2),
      display: 'block',
      margin: 'auto',
      maxWidth: '100%',
      [theme.breakpoints.up('sm')]: {
        maxWidth: 'none !important',
        height: 160,
      },
    },
  })
);

const TOGGLE_TRACK_MUTATION = gql`
  mutation ToggleTrack($id: ID!) {
    trackRequestToggle(trackId: $id) {
      id
      userActions
    }
  }
`;

const GET_TRACK = gql`
  query GetTrack($id: ID!) {
    track(id: $id) {
      id
      userActions
      compRegistration {
        id
        gender
        ageGroup {
          name
        }
        teamDescription
        weightGroupOptions {
          name
          value
          details
        }
        divisionOptions {
          name
          value
          details
        }
        identityNeedsVerification
        identityVerification {
          status
          stripeErrorReason
          rejectedReason
        }
      }
    }
  }
`;

const STEPS = {
  login: 'Login',
  division: 'Division',
  payment: 'Payment',
  verification: 'Verification',
  join: 'Access Request',
  confirmation: 'Confirmation',
};

function buildTrackSteps(
  isPaymentFlow: boolean,
  isComp: boolean,
  requiresVerification: boolean
): string[] {
  const steps: string[] = ['login'];
  if (isComp) steps.push('division');
  if (isPaymentFlow) {
    steps.push('payment');
  } else {
    steps.push('join');
  }
  if (requiresVerification) steps.push('verification');
  steps.push('confirmation');
  return steps;
}

const TrackPage = ({ pageContext }) => {
  const { track } = pageContext;
  const {
    description,
    termsAccepted,
    setTermsAccepted,
    currentUser,
  } = useTrackPage({ pageContext });
  const { redirectToAuth, refetchCurrentUser } = useCurrentUser();
  const price = track.activePrice;
  const activePrices = track.activePrices || [];
  const hasRecurringPrices = activePrices.every((p) => p.recurringMonths);
  const hasPrice = price && (price.amountCents || price.bundle);
  const isPaymentFlow = !!hasPrice;

  const [stripeSuccess] = useQueryParam('stripeSuccess', StringParam);
  const [trackLoaded, setTrackLoaded] = useState(false);

  const [toggleTrackMutation, { loading: toggleLoading }] = useMutation(
    TOGGLE_TRACK_MUTATION
  );

  const { data, refetch } = useQuery(GET_TRACK, {
    variables: { id: track.id },
    fetchPolicy: 'cache-and-network',
    skip: !currentUser,
    onCompleted: () => setTrackLoaded(true),
  });

  const membership =
    currentUser &&
    currentUser.trackMemberships.find(
      (tm) => tm.track.id === track.id && tm.active && !tm.trial
    );

  const isGrantedMembership =
    membership &&
    !!membership.recurring &&
    membership.status === 'access-granted';

  const userActions =
    data && data.track.userActions ? data.track.userActions : [];

  const canJoin = userActions.includes('join');
  const canRequestAccess = userActions.includes('request-access');
  const isPendingApproval = userActions.includes('pending-approval');
  const hasJoined = userActions.includes('joined');
  const isRegistered = isPendingApproval || hasJoined;

  const styles = useStyles();
  const [activeStep, setActiveStep] = useState('login');

  const requiresVerification = track.features.includes('identity-verification');
  const steps = buildTrackSteps(
    isPaymentFlow,
    !!track.compStatus,
    requiresVerification
  );

  const compRegistration = data?.track?.compRegistration;
  const missingCompRegistration =
    trackLoaded && track.compStatus && data && !compRegistration.id;
  const teamDescription =
    track.compStatus && data && compRegistration.teamDescription;

  const showMutationError = (err: {
    graphQLErrors?: { message: string }[];
  }) => {
    const message =
      err.graphQLErrors && err.graphQLErrors.map((e) => e.message).join(', ');
    alert(message || 'Something went wrong. Please try again.');
  };

  useEffect(() => {
    if (isPaymentFlow) {
      if (missingCompRegistration) {
        setActiveStep('division');
      } else if (
        (membership && !isGrantedMembership) ||
        stripeSuccess === 'true'
      ) {
        const needsVerification =
          requiresVerification && compRegistration?.identityNeedsVerification;
        setActiveStep(needsVerification ? 'verification' : 'confirmation');
      } else if (currentUser) {
        setActiveStep('payment');
      }
    } else {
      if (missingCompRegistration) {
        setActiveStep('division');
      } else if (isRegistered) {
        const needsVerification =
          requiresVerification && compRegistration?.identityNeedsVerification;
        setActiveStep(needsVerification ? 'verification' : 'confirmation');
      } else if (currentUser) {
        setActiveStep('join');
      }
    }
  }, [
    currentUser,
    setActiveStep,
    stripeSuccess,
    membership,
    missingCompRegistration,
    isPaymentFlow,
    isRegistered,
    requiresVerification,
    compRegistration,
  ]);

  const allowRegistration = isPaymentFlow
    ? [undefined, null, 'unlisted', 'registration'].includes(track.compStatus)
    : !trackLoaded || canJoin || canRequestAccess || hasJoined || isRegistered;

  const titlePrefixes = isPaymentFlow
    ? [
        track.name,
        track.planWeeks
          ? 'Training Plan'
          : track.compStatus
          ? 'Competition'
          : hasRecurringPrices
          ? 'Programming'
          : '',
        track.source.name,
      ]
    : [track.name as string];

  return (
    <Layout titlePrefixes={titlePrefixes}>
      <Header solid />

      <section className={styles.pageContainer}>
        <Box className={styles.contentContainer}>
          {isPaymentFlow && (
            <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: 20 }}>
              <Link color="inherit" to="/discover">
                Discover
              </Link>
              <Link
                color="inherit"
                to={`/discover?source=${track.source.slug}`}
              >
                {track.source.name}
              </Link>
            </Breadcrumbs>
          )}
          <TrackHeader
            imageUrl={track.imageUrl}
            name={track.name}
            activePrices={isPaymentFlow ? activePrices : undefined}
            price={isPaymentFlow ? price : undefined}
            compDateDisplay={track.compDateDisplay}
            compStatus={track.compStatus}
            sourceName={track.source.name}
          />
          <div>
            {allowRegistration ? (
              <>
                <TrackStepper
                  steps={steps}
                  stepLabels={STEPS}
                  activeStep={activeStep}
                />
                <div>
                  {activeStep === 'login' && (
                    <TrackLoginStep
                      termsAccepted={termsAccepted}
                      onTermsChange={setTermsAccepted}
                      onLogin={redirectToAuth}
                    />
                  )}
                  {activeStep === 'division' && data?.track && (
                    <DivisionStep
                      key={track.id}
                      track={track}
                      compRegistration={compRegistration}
                      currentUser={currentUser}
                      reload={refetch}
                    />
                  )}
                  {activeStep === 'payment' && (
                    <>
                      {isGrantedMembership && (
                        <Alert severity="info" style={{ marginBottom: 20 }}>
                          <AlertTitle>
                            You already have access to{' '}
                            <strong>{track.name}</strong>. If you want to start
                            paying through ErgZone, please continue with the
                            payment process.
                          </AlertTitle>
                        </Alert>
                      )}
                      <PaymentStep
                        key={track.id}
                        track={track}
                        currentUser={currentUser}
                      />
                    </>
                  )}
                  {activeStep === 'verification' && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <IdentityVerificationStep
                        compRegistration={compRegistration}
                        currentUser={currentUser}
                        reload={refetch}
                      />
                    </Box>
                  )}
                  {activeStep === 'join' && (
                    <div>
                      <Box className={styles.instructions}>
                        {currentUser ? (
                          <>
                            Hi <strong>{currentUser.name}</strong>{' '}
                            <em>
                              {' '}
                              ({currentUser.c2Username || currentUser.email})
                            </em>
                            ,{' '}
                          </>
                        ) : (
                          ' '
                        )}
                        please click the button below to
                        {canJoin
                          ? ' join the group.'
                          : canRequestAccess
                          ? ' request access to the group.'
                          : ''}
                      </Box>
                      <Button
                        className={styles.button}
                        variant="contained"
                        disabled={toggleLoading}
                        color="primary"
                        onClick={() =>
                          toggleTrackMutation({
                            variables: { id: track.id },
                          }).catch(showMutationError)
                        }
                      >
                        {canJoin
                          ? 'Join'
                          : canRequestAccess
                          ? 'Request Access'
                          : ''}
                      </Button>
                    </div>
                  )}
                  {activeStep === 'confirmation' && (
                    <>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {isPaymentFlow ? (
                          <Alert severity="success">
                            <AlertTitle>
                              Congratulations
                              {currentUser ? ` ${currentUser.name}` : ''}. You
                              are now signed up for{' '}
                              <strong>{track.name}</strong>!
                            </AlertTitle>
                            Open the ErgZone app{' '}
                            {track.compStatus
                              ? `to see the comp details${
                                  teamDescription
                                    ? ' and create or join a team'
                                    : ''
                                }.`
                              : 'to see the new workouts.'}
                          </Alert>
                        ) : (
                          <>
                            {hasJoined && (
                              <Alert severity="success">
                                <AlertTitle>
                                  Congratulations
                                  {currentUser ? ` ${currentUser.name}` : ''}.
                                  You are now part of{' '}
                                  <strong>'{track.name}'</strong>
                                </AlertTitle>
                                Open the ErgZone app{' '}
                                {track.compStatus
                                  ? 'to see the comp details.'
                                  : 'to see the new workouts.'}
                              </Alert>
                            )}
                            {isPendingApproval && (
                              <Alert severity="success">
                                <AlertTitle>
                                  Hi {currentUser ? ` ${currentUser.name}` : ''}
                                  , your request has been sent and is waiting
                                  approval
                                </AlertTitle>
                                Once an administrator of the group approves your
                                request, you'll be able to see the workouts.
                              </Alert>
                            )}
                          </>
                        )}
                        {isMobile() && (
                          <Button
                            className={styles.openApp}
                            color="primary"
                            href="https://app.erg.zone/"
                            variant="contained"
                          >
                            Open app
                          </Button>
                        )}
                      </Box>
                      {isPaymentFlow && membership && !!membership.description && (
                        <Box className={styles.descriptionContainer}>
                          <Typography
                            variant="h5"
                            className={styles.instructions}
                          >
                            More Information
                          </Typography>
                          <Box className={styles.instructions}>
                            <Linkify>{membership.description}</Linkify>
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                marginTop={2}
              >
                <Alert severity="warning">
                  {isPaymentFlow
                    ? 'Registration is closed!'
                    : 'This group is closed.'}
                </Alert>
                {isPaymentFlow && (
                  <Button
                    style={{ marginTop: 10 }}
                    color="primary"
                    href={`https://comp.erg.zone/leaderboard/${track.idToken}`}
                    variant="contained"
                  >
                    Leaderboard
                  </Button>
                )}
              </Box>
            )}
            <TrackDescription
              description={track.description}
              descriptionHtml={description}
              featuredImage={track.featuredImage}
              trackName={track.name}
            />
            {track.compStatus && track.compStatus !== 'unlisted' && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                marginTop={4}
              >
                <Button
                  color="primary"
                  href={`https://comp.erg.zone/leaderboard/${track.idToken}`}
                  variant="contained"
                >
                  Leaderboard
                </Button>
              </Box>
            )}
          </div>
        </Box>
      </section>
      <Footer />
    </Layout>
  );
};

export default TrackPage;
